import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import { CustomLink } from "../Header";
import { Link } from "react-router-dom";
import MobileOrnamentsSvg from "./MobileHeaderMenuOrnaments";

const MobileHeaderContainer = styled.div`
  position: fixed;
  background-color: #1d1311;
  z-index: 100;
  width: 100%;
  height: 123px;
  display: flex;
  justify-content: center;
`;

const BravoMobileLogo = styled(Link)`
  margin-top: ${({ $scrolled }) => ($scrolled ? "4px" : "9px")};
  background-image: url("/bravo-circle-logo.png");
  background-size: cover;
  z-index: 10;
  border-radius: 50%;
  width: ${({ $scrolled }) => ($scrolled ? "112px" : "192px")};
  height: ${({ $scrolled }) => ($scrolled ? "112px" : "192px")};
  background-color: #1d1311;
  transition: all 0.8s ease-in-out;
`;

const BravoMobileMenuLogo = styled(BravoMobileLogo)`
  position: relative;
  left: -8px;
  top: 9px;
  background-image: url("/bravo-circle-logo.png");
  background-size: cover;
  z-index: 10;
  border-radius: 50%;
  width: 120px;
  height: 120px;
  background-color: transparent;
`;

const MobileLinkContainer = styled.div`
  font-family: "Figtree", sans-serif;
  padding: 36px;
  font-weight: 800;
  font-size: 17px;
  letter-spacing: 2px;
  z-index: 200;
  top: 0;
  /* Modify width to not cover the entire screen, allowing space for the ContactButton */
  width: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: center;

  a {
    font-size: 20px !important;
    position: relative;
    color: white;
    text-decoration: none;
  }

  .active {
    color: #d5eeca;
    border-bottom: 2px solid #d5eeca;
  }
`;

const HamburgerMenuIcon = ({ className, onClick }) => {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onClick={onClick}
    >
      <path d="M3 4H21V6H3V4ZM3 11H21V13H3V11ZM3 18H21V20H3V18Z" fill="white" />
    </svg>
  );
};

const HamburgerMenu = styled(HamburgerMenuIcon)`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 200;
  cursor: pointer;
`;

const MobileMenu = styled.div`
  transform: ${({ $showMenu }) => ($showMenu ? "none" : "translateX(100%)")};
  will-change: transform;
  transition: transform 0.3s ease-in-out;
  position: fixed;
  left: 0;
  left: 0;
  right: 0;
  background-image: url("/Marble-dark.jpg");
  background-size: cover;
  display: flex;
  flex-direction: column;
  z-index: 100;
  padding: 16px;
  overflow: hidden; /* Ensures SVG doesn't cause scrollbars */

  a {
    color: white;
    text-decoration: none;
    margin: 15px 0;
    font-size: 18px;
  }
`;

const MobileOrnaments = styled(MobileOrnamentsSvg)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5; /* Lower z-index so content appears above it */
  pointer-events: none; /* Allows clicking through the SVG to content below */
`;

const BottomRow = styled.div`
  display: flex;
  align-items: flex-end; /* Vertically center the items */
  gap: 20px;
  justify-content: space-between;
`;

const MobileContactButton = styled(Link)`
  color: #1d1311 !important;
  margin: 32px 20px !important;

  letter-spacing: 1px;
  font-family: "Figtree", sans-serif;
  font-weight: 800;
  background: #d5eeca;
  color: #1d1311;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  padding: 6px 14px !important;
  text-decoration: none;
  text-align: center;
  font-size: 17px !important;
  cursor: pointer;
`;

const MobileNavbarSpacer = styled.div`
  height: 123px; /* Same height as the header */
`;
const MobileHeader = () => {
  const [showMenu, setShowMenu] = React.useState(false);
  const [menuDimensions, setMenuDimensions] = useState({ width: 0, height: 0 });
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  useEffect(() => {
    if (showMenu && menuRef.current) {
      const updateDimensions = () => {
        setMenuDimensions({
          width: menuRef.current.offsetWidth,
          height: menuRef.current.offsetHeight,
        });
      };

      // Update dimensions initially and on resize
      updateDimensions();
      window.addEventListener("resize", updateDimensions);

      return () => {
        window.removeEventListener("resize", updateDimensions);
      };
    }
  }, [showMenu]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  return (
    <>
      <MobileHeaderContainer>
        <BravoMobileLogo to="/" $scrolled={scrolled} />
        <HamburgerMenu onClick={toggleMenu} />
        <MobileMenu $showMenu={showMenu} ref={menuRef}>
          <MobileOrnaments
            width={menuDimensions.width}
            height={menuDimensions.height}
          />
          <MobileLinkContainer>
            <span onClick={toggleMenu}>
              <CustomLink to="/">HOME</CustomLink>
            </span>
            <span onClick={toggleMenu}>
              <CustomLink to="/services">SERVICES</CustomLink>
            </span>
            <span onClick={toggleMenu}>
              <CustomLink to="/testimonials">TESTIMONIALS</CustomLink>
            </span>
          </MobileLinkContainer>
          <BottomRow>
            <BravoMobileMenuLogo />
            <MobileContactButton to="/contact" onClick={toggleMenu}>
              CONTACT
            </MobileContactButton>
          </BottomRow>
        </MobileMenu>
      </MobileHeaderContainer>
      <MobileNavbarSpacer />
    </>
  );
};

export default MobileHeader;
